/**
 * Decides whether an authenticated user should be stopped at the
 * "your account isn't set up yet" screen.
 *
 * Kept as a pure function so the outage-safety rule below is directly testable: an empty
 * permission set must only be treated as real once the server has actually answered.
 */
export interface AccessGateInput {
  /** True only once application-configuration was fetched successfully. */
  permissionsResolved: boolean;
  /** Granted permissions from application-configuration. */
  permissions: string[];
}

/**
 * True when application-configuration treated the token we sent as anonymous.
 *
 * That endpoint allows anonymous callers, so the API answers a token it will not accept
 * (bad signature, wrong issuer, expired by the server's clock) with 200 and no permissions.
 * This is a dead session, not an answer about the user's access, whatever their account holds.
 */
export const isTokenRejected = (
  appConfig: { currentUser?: { isAuthenticated?: boolean } | null } | null | undefined,
): boolean => appConfig?.currentUser?.isAuthenticated === false;

export const shouldBlockAsUnconfigured = ({
  permissionsResolved,
  permissions,
}: AccessGateInput): boolean => {
  // Before the server has answered we know nothing. Blocking here would turn any
  // transient failure of /api/abp/application-configuration into a lockout for
  // every user, so the unknown case must always fall through.
  if (!permissionsResolved) {
    return false;
  }

  return permissions.length === 0;
};
